import React, { useState, useEffect, useRef } from "react";
import MDEditor, { commands } from '@uiw/react-md-editor';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
  Alert,
  Autocomplete,
  Chip,
  Grid,
} from "@mui/material";
import { Icon } from "../../../icons";
import { COLORS, TYPOGRAPHY } from "../../../constants/color";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import { useAuth } from "../../../context/authContext";
import { url } from "../../../constants/url";
import AppToast from "../../../components/AppToast";
import PhotoUploadWithDrawing from "../../../components/siteCheck/PhotoUploadWithDrawing";
import ImagePreviewModal from "../../../components/ui/ImagePreviewModal";
import SignatureCanvas from "../../../components/siteCheck/SignatureCanvas";
import { useNavigate } from "react-router-dom";
import useFormPersistence from "../../../hooks/useFormPersistence";

const filter = (options, params) => {
  const filtered = options.filter((option) => {
    const inputValue = params.inputValue.toLowerCase();
    return option.projectName.toLowerCase().includes(inputValue);
  });

  const { inputValue } = params;
  // Suggest the creation of a new value
  const isExisting = options.some((option) => inputValue.toLowerCase() === option.projectName.toLowerCase());
  if (inputValue !== '' && !isExisting) {
    filtered.push({
      projectName: inputValue,
      isNew: true,
    });
  }

  return filtered;
};

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    "& fieldset": {
      borderColor: COLORS.PRIMARY,
    },
    "&:hover fieldset": {
      borderColor: COLORS.PRIMARY,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.PRIMARY,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    opacity: 1,
  },
  "& .MuiInputBase-input": {
    color: COLORS.PRIMARY,
  },
};

const dateInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    "& fieldset": {
      borderColor: `${COLORS.PRIMARY} !important`,
    },
    "&:hover fieldset": {
      borderColor: `${COLORS.PRIMARY} !important`,
    },
    "&.Mui-focused fieldset": {
      borderColor: `${COLORS.PRIMARY} !important`,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    opacity: 1,
  },
};

// Helper function to convert file path to full URL
const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // If it's already a data URL or full URL, return as-is
  if (imagePath.startsWith("data:image") ||
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://")) {
    return imagePath;
  }

  // If it's a file path, construct full URL
  const normalizedPath = imagePath.replace(/\\/g, '/');
  const cleanPath = normalizedPath.startsWith("/")
    ? normalizedPath.substring(1)
    : normalizedPath;

  // Check if it already has the base URL
  if (cleanPath.startsWith("uploads/")) {
    return `${url}/${cleanPath}`;
  }

  return `${url}/uploads/${cleanPath}`;
};

// Helper function to convert base64 to blob
const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

// Signature Modal Component
const SignatureModal = ({
  open,
  onClose,
  onSave,
  role,
  userData,
  pocName,
  assignedEngineerName,
}) => {
  const canvasRef = useRef(null);
  const [signature, setSignature] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) {
      if (role === "pmcEngineer") {
        setName(assignedEngineerName || userData?.name || "");
      } else if (role === "siteEngineer") {
        setName(pocName || "");
      } else if (role === "pmc") {
        setName("PMC");
      }

      if (canvasRef.current) {
        canvasRef.current.clear();
        setSignature(null);
      }
    }
  }, [open, role, userData, pocName, assignedEngineerName]);

  const handleClose = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
    setSignature(null);
    onClose();
  };

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      setSignature(null);
    }
  };

  const handleSave = () => {
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");

      const saveData = {
        signature: dataUrl,
        name: name.trim(),
      };

      onSave(saveData, role);
      handleClose();
    }
  };

  const getModalTitle = () => {
    switch (role) {
      case "pmcEngineer":
        return `Sign as PMC Engineer (${assignedEngineerName || userData?.name || "Engineer"})`;
      case "siteEngineer":
        return `Sign for Site Engineer (${pocName || "POC"})`;
      case "pmc":
        return "Sign as PMC";
      default:
        return "Add Signature";
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        bgcolor: "rgba(0,0,0,0.5)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 500,
          maxHeight: "95vh",
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
            {getModalTitle()}
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 2 }}>
            <Icon name="close" />
          </IconButton>
        </Box>

        <Box
          sx={{
            p: 2,
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {(role === "siteEngineer" || role === "pmcEngineer") && (
            <TextField
              fullWidth
              label={`${role === "pmcEngineer" ? "PMC Engineer" : "Site Engineer"} Name *`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`Enter ${role === "pmcEngineer" ? "PMC Engineer" : "Site Engineer"}'s full name`}
              size="small"
              sx={{ mb: 3 }}
              required
            />
          )}

          <Typography
            sx={{
              ...TYPOGRAPHY.BODY_SMALL,
              color: COLORS.TEXT,
              mb: 1.5,
            }}
          >
            Draw your signature in the box below
          </Typography>

          <Box
            sx={{
              flex: 1,
              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: "#f8f8f8",
              position: "relative",
              height: 200,
            }}
          >
            <SignatureCanvas
              ref={canvasRef}
              onSignatureChange={setSignature}
              canvasProps={{
                style: {
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#fff",
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            flexShrink: 0,
          }}
        >
          <Button onClick={handleClose} size="small">
            Cancel
          </Button>
          <Button onClick={handleClear} color="error" size="small">
            Clear
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!signature || !name.trim()}
            sx={{
              bgcolor: COLORS.SECONDARY,
              "&.Mui-disabled": {
                bgcolor: COLORS.LIGHT_SECONDARY,
              },
            }}
            size="small"
          >
            Save Signature
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Signatures Display Component
const SignaturesDisplay = ({
  signatures,
  onAddSignature,
  onApplyStoredSignature,
  userRole,
  userData,
  pocName,
  assignedEngineerName,
  userSignature,
  adminSignature,
  loadingSignatures,
}) => {
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState(null);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const dateStr = date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
      .replace(/\//g, "/");
    const timeStr = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} | ${timeStr}`;
  };

  const shouldDisplaySignature = (role) => {
    return !!signatures?.[role]?.signature;
  };

  const canSign = (role) => {
    if (role === "pmcEngineer") {
      return userRole === "ENGINEER" || userRole === "ADMIN" || userRole === "SUPERADMIN";
    } else if (role === "siteEngineer") {
      return userRole === "ENGINEER" || userRole === "ADMIN" || userRole === "SUPERADMIN";
    } else if (role === "pmc") {
      return userRole === "ADMIN" || userRole === "SUPERADMIN";
    }
    return false;
  };

  const hasStoredSignature = (role) => {
    if (role === "pmcEngineer") {
      return userSignature?.hasSignature && userSignature?.signature;
    } else if (role === "pmc") {
      return adminSignature?.hasSignature && adminSignature?.signature;
    }
    return false;
  };

  const getDisplayName = (role, signatureData) => {
    if (signatureData?.name) {
      return signatureData.name;
    }

    switch (role) {
      case "pmcEngineer":
        return assignedEngineerName || "PMC Engineer";
      case "siteEngineer":
        return pocName || "Site Engineer";
      case "pmc":
        return "PMC";
      default:
        return "Not signed";
    }
  };

  const handleSignClick = (role) => {
    if (!canSign(role)) {
      return;
    }

    if (role === "siteEngineer") {
      setCurrentSignatureRole(role);
      setSignatureModalOpen(true);
      return;
    }

    // For PMC and PMC Engineer, check if user has stored signature
    if (hasStoredSignature(role)) {
      onApplyStoredSignature(role);
    } else {
      setCurrentSignatureRole(role);
      setSignatureModalOpen(true);
    }
  };

  const getButtonText = (role) => {
    if (loadingSignatures) {
      return "Loading...";
    }

    switch (role) {
      case "pmcEngineer":
        return hasStoredSignature(role) ? "Sign as PMC Engineer" : "Sign as PMC Engineer";
      case "siteEngineer":
        return "Sign for Site Engineer";
      case "pmc":
        return hasStoredSignature(role) ? "Sign as PMC" : "Sign as PMC";
      default:
        return "Sign";
    }
  };

  const { siteEngineer, pmcEngineer, pmc } = signatures || {};

  return (
    <>
      <Box sx={{ mt: 4 }}>
        <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 3 }}>
          Signatures
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 4,
            textAlign: "center",
          }}
        >
          {/* PMC Engineer Signature */}
          <Box>
            {shouldDisplaySignature("pmcEngineer") ? (
              <Box
                sx={{
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={getFullImageUrl(pmcEngineer?.signature)}
                  alt="PMC Engineer Signature"
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 80,
                  borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              />
            )}
            <Typography
              sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}
            >
              PMC Engineer
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                color: COLORS.PRIMARY,
                mt: 0.5,
                fontWeight: 500,
              }}
            >
              {getDisplayName("pmcEngineer", pmcEngineer)}
            </Typography>
            {pmcEngineer?.signedAt && (
              <Typography
                sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}
              >
                {formatDateTime(pmcEngineer.signedAt)}
              </Typography>
            )}
            {canSign("pmcEngineer") && !shouldDisplaySignature("pmcEngineer") && (
              <Button
                size="small"
                variant="outlined"
                textTransform="none"
                onClick={() => handleSignClick("pmcEngineer")}
                sx={{ mt: 1 }}
                disabled={loadingSignatures}
              >
                {getButtonText("pmcEngineer")}
              </Button>
            )}
          </Box>

          {/* Site Engineer Signature */}
          <Box>
            {shouldDisplaySignature("siteEngineer") ? (
              <Box
                sx={{
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={getFullImageUrl(siteEngineer?.signature)}
                  alt="Site Engineer Signature"
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 80,
                  borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              />
            )}
            <Typography
              sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}
            >
              Site Engineer
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                color: COLORS.PRIMARY,
                mt: 0.5,
                fontWeight: 500,
              }}
            >
              {getDisplayName("siteEngineer", siteEngineer)}
            </Typography>
            {siteEngineer?.signedAt && (
              <Typography
                sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}
              >
                {formatDateTime(siteEngineer.signedAt)}
              </Typography>
            )}
            {canSign("siteEngineer") &&
              !shouldDisplaySignature("siteEngineer") && (
                <Button
                  size="small"
                  variant="outlined"
                  textTransform="none"
                  onClick={() => handleSignClick("siteEngineer")}
                  sx={{ mt: 1 }}
                  disabled={loadingSignatures}
                >
                  {getButtonText("siteEngineer")}
                </Button>
              )}
          </Box>

          {/* PMC Signature */}
          <Box>
            {shouldDisplaySignature("pmc") ? (
              <Box
                sx={{
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={getFullImageUrl(pmc?.signature)}
                  alt="PMC Signature"
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 80,
                  borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              />
            )}
            <Typography
              sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mt: 1 }}
            >
              PMC
            </Typography>
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                color: COLORS.PRIMARY,
                mt: 0.5,
                fontWeight: 500,
              }}
            >
              {getDisplayName("pmc", pmc)}
            </Typography>
            {pmc?.signedAt && (
              <Typography
                sx={{ ...TYPOGRAPHY.CAPTION, color: COLORS.SECONDARY, mt: 0.5 }}
              >
                {formatDateTime(pmc.signedAt)}
              </Typography>
            )}
            {canSign("pmc") && !shouldDisplaySignature("pmc") && (
              <Button
                size="small"
                textTransform="none"
                variant="outlined"
                onClick={() => handleSignClick("pmc")}
                sx={{ mt: 1 }}
                disabled={loadingSignatures}
              >
                {getButtonText("pmc")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Signature Modal */}
      {signatureModalOpen && (
        <SignatureModal
          open={signatureModalOpen}
          onClose={() => {
            setSignatureModalOpen(false);
            setCurrentSignatureRole(null);
          }}
          onSave={(signatureData) => {
            onAddSignature(signatureData, currentSignatureRole);
            setSignatureModalOpen(false);
            setCurrentSignatureRole(null);
          }}
          role={currentSignatureRole}
          userData={userData}
          pocName={pocName}
          assignedEngineerName={assignedEngineerName}
        />
      )}
    </>
  );
};

// Solution Renderer Component
const SolutionRenderer = ({ solution }) => {
  if (!solution) return null;

  try {
    if (typeof solution === 'string' && solution.trim().startsWith('{') && solution.trim().endsWith('}')) {
      const structured = JSON.parse(solution);
      if (structured.isStructured) {
        if (structured.type === 'points' && Array.isArray(structured.data)) {
          return (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {structured.data.map((point, i) => (
                <Typography component="li" key={i} sx={{ color: COLORS.PRIMARY, mb: 0.5 }}>
                  {point}
                </Typography>
              ))}
            </Box>
          );
        }
        if (structured.type === 'table' && Array.isArray(structured.data)) {
          return (
            <Box sx={{ mt: 1, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                <tbody>
                  {structured.data.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} style={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, padding: '8px', fontSize: '0.875rem', color: COLORS.PRIMARY }}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          );
        }
      }
    }
  } catch (e) {
    // Fallback to text
  }

  return (
    <Box data-color-mode="light" sx={{ color: COLORS.PRIMARY }}>
      <MDEditor.Markdown source={solution} style={{ whiteSpace: 'pre-wrap', backgroundColor: 'transparent', color: 'inherit' }} />
    </Box>
  );
};

// Points Editor Component
const PointsEditor = ({ points, setPoints }) => {
  const handleAddPoint = () => setPoints([...points, ""]);
  const handleRemovePoint = (index) => {
    const newPoints = points.filter((_, i) => i !== index);
    setPoints(newPoints.length > 0 ? newPoints : [""]);
  };
  const handleChangePoint = (index, value) => {
    const newPoints = [...points];
    newPoints[index] = value;
    setPoints(newPoints);
  };

  return (
    <Box>
      {points.map((point, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Point ${index + 1}`}
            value={point}
            onChange={(e) => handleChangePoint(index, e.target.value)}
            sx={inputStyles}
          />
          <IconButton onClick={() => handleRemovePoint(index)} color="error" size="small">
            <Icon name="close2" size={18} />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<Icon name="plus" />} onClick={handleAddPoint} size="small" sx={{ color: COLORS.SECONDARY, textTransform: 'none' }}>
        Add Point
      </Button>
    </Box>
  );
};

// Table Editor Component
const TableEditor = ({ tableData, setTableData, columns, setColumns }) => {
  const handleAddRow = () => {
    setTableData([...tableData, Array(columns).fill("")]);
  };
  const handleRemoveRow = (index) => {
    const newData = tableData.filter((_, i) => i !== index);
    setTableData(newData.length > 0 ? newData : [Array(columns).fill("")]);
  };
  const handleChangeCell = (rowIndex, colIndex, value) => {
    const newData = [...tableData];
    newData[rowIndex][colIndex] = value;
    setTableData(newData);
  };

  const handleColumnChange = (newCols) => {
    const cols = Math.max(1, Math.min(5, newCols));
    setColumns(cols);
    const newData = tableData.map(row => {
      if (row.length < cols) {
        return [...row, ...Array(cols - row.length).fill("")];
      }
      return row.slice(0, cols);
    });
    setTableData(newData);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Columns:</Typography>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 1, max: 5 }}
          value={columns}
          onChange={(e) => handleColumnChange(parseInt(e.target.value) || 1)}
          sx={{ ...inputStyles, width: 80 }}
        />
      </Box>
      <Box sx={{ overflowX: 'auto', border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {tableData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex} style={{ padding: '8px', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      value={cell}
                      onChange={(e) => handleChangeCell(rowIndex, colIndex, e.target.value)}
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      placeholder="Cell content"
                      sx={{ "& .MuiInputBase-input": { fontSize: '0.875rem' } }}
                    />
                  </td>
                ))}
                <td style={{ padding: '4px', textAlign: 'center', width: 40 }}>
                  <IconButton onClick={() => handleRemoveRow(rowIndex)} color="error" size="small">
                    <Icon name="close2" size={18} />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Button startIcon={<Icon name="plus" />} onClick={handleAddRow} size="small" sx={{ color: COLORS.SECONDARY, mt: 1, textTransform: 'none' }}>
        Add Row
      </Button>
    </Box>
  );
};

// Simple Entry Form Component
const EntryForm = ({ index, onSave, onCancel, onAddPhoto, data, onDataChange }) => {
  const {
    test = "",
    solutionType = "text",
    solutionText = "",
    solutionPoints = [""],
    solutionTable = [["", ""]],
    tableCols = 2,
    photos = []
  } = data || {};

  // Image preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", title: "" });

  const updateField = (field, value) => {
    onDataChange(prev => ({ ...prev, [field]: value }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = e.target;
      const start = textarea.selectionStart;
      const value = textarea.value;
      
      const beforeCursor = value.substring(0, start);
      const currentLineStart = beforeCursor.lastIndexOf('\n') + 1;
      const currentLine = beforeCursor.substring(currentLineStart);
      
      const bulletMatch = currentLine.match(/^(\s*)([-*+])\s(.*)$/);
      const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s(.*)$/);
      
      if (bulletMatch || numberMatch) {
        e.preventDefault();
        
        let insertText = '';
        if (bulletMatch) {
          if (!bulletMatch[3].trim()) {
            const newValue = value.substring(0, currentLineStart) + value.substring(start);
            updateField('solutionText', newValue);
            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd = currentLineStart;
            }, 0);
            return;
          }
          insertText = `\n${bulletMatch[1]}${bulletMatch[2]} `;
        } else if (numberMatch) {
          if (!numberMatch[3].trim()) {
            const newValue = value.substring(0, currentLineStart) + value.substring(start);
            updateField('solutionText', newValue);
            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd = currentLineStart;
            }, 0);
            return;
          }
          const nextNum = parseInt(numberMatch[2], 10) + 1;
          insertText = `\n${numberMatch[1]}${nextNum}. `;
        }
        
        const newValue = value.substring(0, start) + insertText + value.substring(start);
        updateField('solutionText', newValue);
        
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + insertText.length;
        }, 0);
      }
    }
  };

  const handleSave = () => {
    if (!test.trim()) return;

    let finalSolution = "";
    if (solutionType === "text") {
      if (!solutionText.trim()) return;
      finalSolution = solutionText.trim();
    } else if (solutionType === "points") {
      const filteredPoints = solutionPoints.filter(p => p.trim() !== "");
      if (filteredPoints.length === 0) return;
      finalSolution = JSON.stringify({
        isStructured: true,
        type: "points",
        data: filteredPoints
      });
    } else if (solutionType === "table") {
      const filteredTable = solutionTable.filter(row => row.some(cell => cell.trim() !== ""));
      if (filteredTable.length === 0) return;
      finalSolution = JSON.stringify({
        isStructured: true,
        type: "table",
        columns: tableCols,
        data: filteredTable
      });
    }

    onSave({
      test: test.trim(),
      solution: finalSolution,
      photos: photos,
      title: `Test ${index + 1}`
    });
  };

  const handleRemovePhoto = (photoIndex) => {
    onDataChange(prev => {
      const updatedPhotos = [...(prev.photos || [])];
      updatedPhotos.splice(photoIndex, 1);
      return { ...prev, photos: updatedPhotos };
    });
  };

  return (
    <Box sx={{ mb: 3, p: 2, border: `1px solid ${COLORS.PRIMARY}`, borderRadius: 2 }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2 }}>
        {index + 1}.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1, fontWeight: 600 }}
        >
          Test
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Enter test details..."
          value={test}
          onChange={(e) => updateField('test', e.target.value)}
          sx={inputStyles}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}
          >
            Solution
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['text', 'points', 'table'].map((type) => (
              <Chip
                key={type}
                label={type.charAt(0).toUpperCase() + type.slice(1)}
                onClick={() => updateField('solutionType', type)}
                size="small"
                sx={{
                  bgcolor: solutionType === type ? COLORS.PRIMARY : 'transparent',
                  color: solutionType === type ? '#fff' : COLORS.PRIMARY,
                  border: `1px solid ${COLORS.PRIMARY}`,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: solutionType === type ? COLORS.PRIMARY : 'rgba(66, 26, 110, 0.04)',
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {solutionType === "text" && (
          <Box data-color-mode="light" sx={{ border: `1px solid ${COLORS.PRIMARY}`, borderRadius: '5px', overflow: 'hidden', '.w-md-editor': { border: 'none', borderRadius: 0 } }}>
            <MDEditor
              value={solutionText}
              onChange={(val) => updateField('solutionText', val || '')}
              height={250}
              preview="edit"
              extraCommands={[]}
              commands={[
                commands.bold,
                commands.italic,
                commands.strikethrough,
                commands.divider,
                commands.unorderedListCommand,
                commands.orderedListCommand,
                commands.checkedListCommand,
              ]}
              textareaProps={{
                onKeyDown: handleKeyDown
              }}
            />
          </Box>
        )}

        {solutionType === "points" && (
          <PointsEditor points={solutionPoints} setPoints={(pts) => updateField('solutionPoints', pts)} />
        )}

        {solutionType === "table" && (
          <TableEditor 
            tableData={solutionTable} 
            setTableData={(tbl) => updateField('solutionTable', tbl)} 
            columns={tableCols} 
            setColumns={(cols) => updateField('tableCols', cols)} 
          />
        )}
      </Box>

      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          textTransform="none"
          startIcon={<Icon name="camera" />}
          onClick={onAddPhoto}
          disabled={photos?.length >= 5}
          sx={{
            borderColor: COLORS.SECONDARY,
            color: COLORS.SECONDARY,
            "&:hover": {
              borderColor: COLORS.PRIMARY,
              color: COLORS.PRIMARY,
            },
          }}
        >
          Add Photo ({photos?.length || 0}/5)
        </Button>
      </Box>

      {photos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1, fontWeight: 600 }}>
            Added Photos
          </Typography>
          <Grid container spacing={2}>
            {photos.map((photo, photoIndex) => (
              <Grid item xs={12} sm={6} md={4} key={photoIndex}>
                <Box
                  onClick={() => {
                    const imageUrl = photo.imageUrl || photo.photoPath || photo;
                    setSelectedImage({ url: imageUrl, title: photo.title || photo.remarks || "" });
                    setPreviewOpen(true);
                  }}
                  sx={{
                    position: "relative",
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: 1,
                    overflow: "hidden",
                    height: "180px",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "#f5f5f5",
                    cursor: "zoom-in",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "scale(1.02)",
                      borderColor: COLORS.PRIMARY,
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={photo.imageUrl || photo.photoPath || photo}
                    alt={`Photo ${photoIndex + 1}`}
                    sx={{
                      width: "100%",
                      height: (photo.title || photo.remarks) ? "130px" : "100%",
                      objectFit: "contain",
                    }}
                  />
                  {(photo.title || photo.remarks) && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        color: COLORS.TEXT,
                        p: 1,
                        borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {photo.title || photo.remarks}
                      </Typography>
                    </Box>
                  )}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePhoto(photoIndex);
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255, 0, 0, 0.7)",
                      color: "#fff",
                      width: 24,
                      height: 24,
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.9)",
                      },
                    }}
                    size="small"
                  >
                    <Icon name="close" size={14} />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Button Container - Cancel left, Save right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          mt: 3,
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          startIcon={<Icon name="close2" size={18} />}
          sx={{
            height: 36,
            minWidth: 110,
            px: 2,
            color: COLORS.ERROR,
            borderColor: COLORS.ERROR,
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              borderColor: COLORS.ERROR,
              backgroundColor: "rgba(211, 47, 47, 0.04)",
            },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSave}
          startIcon={<Icon name="check" size={18} />}
          variant="contained"
          disabled={!test.trim() || (solutionType === 'text' && !solutionText.trim())}
          sx={{
            height: 36,
            minWidth: 110,
            px: 2,
            bgcolor: COLORS.SECONDARY,
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              bgcolor: COLORS.PRIMARY,
            },
            "&.Mui-disabled": {
              bgcolor: COLORS.LIGHT_SECONDARY,
              color: "#fff",
            },
          }}
        >
          Save Entry
        </Button>
      </Box>

      <ImagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageUrl={selectedImage.url}
        title={selectedImage.title}
      />
    </Box>
  );
};

// Display Entry Component
const DisplayEntry = ({ entry, index, onDelete, onEdit, draggable, onDragStart }) => {
  // Image preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", title: "" });

  return (
    <Box 
      draggable={draggable}
      onDragStart={onDragStart}
      sx={{ 
        mb: 3, p: 2, bgcolor: 'rgba(66, 26, 110, 0.02)', 
        borderRadius: 2, borderLeft: `4px solid ${COLORS.PRIMARY}`,
        cursor: draggable ? 'grab' : 'default'
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1,
        }}
      >
        <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT, fontWeight: 700 }}>
          {index + 1}. {entry.test}
        </Typography>
        <Box sx={{ display: 'flex' }}>
          {onEdit && (
            <IconButton
              onClick={() => onEdit(index)}
              size="small"
              sx={{ color: COLORS.SECONDARY, mt: -0.5, mr: 1 }}
            >
              <Icon name="edit" />
            </IconButton>
          )}
          <IconButton
            onClick={() => onDelete(index)}
            size="small"
            sx={{ color: COLORS.ERROR, mt: -0.5 }}
          >
            <Icon name="close2" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mb: 2, pl: 2 }}>
        <SolutionRenderer solution={entry.solution} />
      </Box>

      {entry.photos?.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Grid container spacing={2}>
            {entry.photos.map((photo, photoIndex) => {
              const imageUrl = photo.imageUrl || photo.photoPath || photo;
              return (
                <Grid item xs={12} sm={6} md={4} key={photoIndex}>
                  <Box
                    onClick={() => {
                      setSelectedImage({ url: imageUrl, title: photo.title || photo.remarks || "" });
                      setPreviewOpen(true);
                    }}
                    sx={{
                      position: "relative",
                      border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      borderRadius: 1,
                      overflow: "hidden",
                      height: "180px",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "#f5f5f5",
                      cursor: "zoom-in",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.02)",
                        borderColor: COLORS.PRIMARY,
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={`Photo ${photoIndex + 1}`}
                      sx={{
                        width: "100%",
                        height: (photo.title || photo.remarks) ? "130px" : "100%",
                        objectFit: "contain",
                      }}
                    />
                  {(photo.title || photo.remarks) && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        color: COLORS.TEXT,
                        p: 1,
                        borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {photo.title || photo.remarks}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
        </Box>
      )}

      <ImagePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageUrl={selectedImage.url}
        title={selectedImage.title}
      />
    </Box>
  );
};

// Main Component
const AddSelfVisitReport = () => {
  const { token, userData, hasRole } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingSignatures, setLoadingSignatures] = useState(true);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastTitle, setToastTitle] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  // 1. Separate persistence for Project Name to drive the draft key
  const [projectName, setProjectName, clearProjectName] = useFormPersistence('site-visit-project-name', "");
  
  // 2. Draft key based on Project Name
  const persistenceKey = `site-visit-report-draft-${projectName || 'new'}`;

  // 3. Consolidated draft state (excluding projectName which is already handled)
  const [formState, setFormState, clearPersistenceDraft, isDraftLoaded] = useFormPersistence(persistenceKey, {
    location: "",
    dateOfVisitStored: null,
    developers: "",
    pmc: "Arconest Consultants",
    purposeOfVisit: "",
    categories: [],
    signatures: {
      siteEngineer: null,
      pmcEngineer: null,
      pmc: null,
    },
    pocName: "",
    currentEntry: {
      test: "",
      solutionType: "text",
      solutionText: "",
      solutionPoints: [""],
      solutionTable: [["", ""]],
      tableCols: 2,
      photos: [],
    }
  }, {
    useIndexedDB: true
  });

  const {
    location,
    dateOfVisitStored,
    developers,
    pmc,
    purposeOfVisit,
    categories = [],
    signatures,
    pocName,
    currentEntry
  } = formState;

  const updateFormState = (updates) => {
    setFormState(prev => ({ ...prev, ...updates }));
  };

  const setLocation = (val) => updateFormState({ location: val });
  const setDateOfVisitStored = (val) => updateFormState({ dateOfVisitStored: val });
  const setDevelopers = (val) => updateFormState({ developers: val });
  const setPmc = (val) => updateFormState({ pmc: val });
  const setPurposeOfVisit = (val) => updateFormState({ purposeOfVisit: val });
  const setCategories = (updater) => {
    setFormState(prev => {
      const newCats = typeof updater === 'function' ? updater(prev.categories || []) : updater;
      return { ...prev, categories: newCats };
    });
  };
  const setSignatures = (val) => updateFormState({ signatures: typeof val === 'function' ? val(signatures) : val });
  const setPocName = (val) => updateFormState({ pocName: val });
  const setCurrentEntry = (val) => {
    setFormState(prev => ({
      ...prev,
      currentEntry: typeof val === 'function' ? val(prev.currentEntry) : val
    }));
  };

  // Date object wrapper for DatePicker
  const dateOfVisit = dateOfVisitStored ? new Date(dateOfVisitStored) : null;
  const setDateOfVisit = (val) => setDateOfVisitStored(val ? (val instanceof Date ? val.toISOString() : val) : null);

  
  const [assignedEngineerName, setAssignedEngineerName] = useState("");

  // Stored signatures from UserSignature model
  const [userSignature, setUserSignature] = useState({
    hasSignature: false,
    signature: null,
    userName: "",
  });

  const [adminSignature, setAdminSignature] = useState({
    hasSignature: false,
    signature: null,
    userName: "",
  });

  const [searchableProjects, setSearchableProjects] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [selectedCategoryIdx, setSelectedCategoryIdx] = useState(0);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [editingEntryInfo, setEditingEntryInfo] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // Entries for the currently selected category (derived)
  const entries = categories[selectedCategoryIdx]?.entries || [];

  // Fetch searchable projects on mount
  useEffect(() => {
    fetchSearchableProjects();
  }, []);

  // Auto-show entry form if there's an active draft entry
  useEffect(() => {
    if (currentEntry) {
      const hasData = 
        currentEntry.test?.trim() || 
        currentEntry.solutionText?.trim() || 
        (currentEntry.photos && currentEntry.photos.length > 0) ||
        (currentEntry.solutionPoints && currentEntry.solutionPoints.some(p => p.trim() !== "")) ||
        (currentEntry.solutionType === 'table' && currentEntry.solutionTable && currentEntry.solutionTable.some(row => row.some(cell => cell.trim() !== "")));
      
      if (hasData) {
        setShowEntryForm(true);
      }
    }
  }, []); // Only run once on mount to restore visibility

  const fetchSearchableProjects = async () => {
    setIsSearchLoading(true);
    try {
      const response = await axios.get(`${url}/api/master-projects/searchable?exclude=Site Visit`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      if (response.data.success) {
        setSearchableProjects(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching searchable projects:", error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleProjectSelect = (event, newValue) => {
    if (!newValue) {
      setProjectName("");
      return;
    }

    const { projectName, address, clientName, companyName, isNew } = newValue;

    if (isNew) {
      setProjectName(projectName);
      return;
    }

    // Pre-fill relatable fields
    setProjectName(projectName || "");
    setLocation(address || location);
    setDevelopers(companyName || developers);

    // Note: We don't pre-fill POC or other details as requested for self-visit context previously, 
    // but here in AddSelfVisitReport, pre-filling location and developers is fine.
  };

  const showToast = (type, title, message) => {
    setToastType(type);
    setToastTitle(title);
    setToastMessage(message);
    setToastOpen(true);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  // Get user role
  const getUserRole = () => {
    if (hasRole("ADMIN")) return "ADMIN";
    if (hasRole("SUPERADMIN")) return "SUPERADMIN";
    if (hasRole("ENGINEER")) return "ENGINEER";
    return null;
  };

  // Fetch stored signature from UserSignature model
  const fetchUserSignature = async () => {
    try {
      setLoadingSignatures(true);
      const userRole = getUserRole();

      if (userRole === "ENGINEER" || userRole === "ADMIN" || userRole === "SUPERADMIN") {
        console.log("Fetching signature for user:", userData._id, "role:", userRole);

        const response = await axios.get(
          `${url}/api/users/signature/${userData._id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000
          }
        );

        console.log("Signature fetch response:", response.data);

        if (response.data?.data) {
          if (userRole === "ENGINEER") {
            setUserSignature({
              hasSignature: response.data.data.hasSignature || false,
              signature: response.data.data.signature, // This could be file path or base64
              userName: response.data.data.userName || userData?.name || "Engineer",
            });
            console.log("Set ENGINEER signature:", response.data.data);
          } else if (userRole === "ADMIN" || userRole === "SUPERADMIN") {
            setAdminSignature({
              hasSignature: response.data.data.hasSignature || false,
              signature: response.data.data.signature, // This could be file path or base64
              userName: response.data.data.userName || "PMC",
            });
            console.log("Set ADMIN signature:", response.data.data);
          }
        } else {
          console.log("No signature data found in response");
        }
      }
    } catch (err) {
      console.error("Could not fetch user signature:", err.message);
      showToast("error", "Signature Error", "Failed to load stored signature");
    } finally {
      setLoadingSignatures(false);
    }
  };

  useEffect(() => {
    fetchUserSignature();
    setAssignedEngineerName(userData?.name || "Engineer");
  }, []);

  // Handle photo upload for an entry
  const handlePhotoUpload = (photoData) => {
    const newPhoto = {
      imageUrl: photoData.imageUrl,
      title: photoData.title || "",
      remarks: photoData.title || "",
      description: photoData.title || "",
    };

    setCurrentEntry(prev => ({
      ...prev,
      photos: [...(prev.photos || []), newPhoto]
    }));
    
    setPhotoModalOpen(false);
    showToast("success", "Photo Added", "Photo uploaded successfully.");
  };

  // Add new entry
  const handleAddNewEntry = () => {
    setShowEntryForm(true);
    setEditingEntryInfo(null);
    setCurrentEntry({
      test: "",
      solutionType: "text",
      solutionText: "",
      solutionPoints: [""],
      solutionTable: [["", ""]],
      tableCols: 2,
      photos: [],
    });
  };

  // Edit an existing entry
  const handleEditEntry = (catIdx, entryIdx) => {
    const entryToEdit = categories[catIdx].entries[entryIdx];
    
    let parsedSolutionType = "text";
    let parsedSolutionText = "";
    let parsedSolutionPoints = [""];
    let parsedSolutionTable = [["", ""]];
    let parsedTableCols = 2;

    try {
      if (typeof entryToEdit.solution === 'string' && entryToEdit.solution.trim().startsWith('{') && entryToEdit.solution.trim().endsWith('}')) {
        const structured = JSON.parse(entryToEdit.solution);
        if (structured.isStructured) {
          if (structured.type === 'points') {
            parsedSolutionType = "points";
            parsedSolutionPoints = structured.data || [""];
          } else if (structured.type === 'table') {
            parsedSolutionType = "table";
            parsedSolutionTable = structured.data || [["", ""]];
            parsedTableCols = structured.columns || 2;
          }
        }
      } else {
        parsedSolutionText = entryToEdit.solution || "";
      }
    } catch (e) {
      parsedSolutionText = entryToEdit.solution || "";
    }

    setCurrentEntry({
      test: entryToEdit.test || entryToEdit.title || "",
      solutionType: parsedSolutionType,
      solutionText: parsedSolutionText,
      solutionPoints: parsedSolutionPoints,
      solutionTable: parsedSolutionTable,
      tableCols: parsedTableCols,
      photos: entryToEdit.photos || [],
    });

    setEditingEntryInfo({ catIdx, entryIdx });
    setSelectedCategoryIdx(catIdx);
    setShowEntryForm(true);
  };

  // Save entry
  const handleSaveEntry = (entryData) => {
    const entryWithPhotos = {
      ...entryData,
      photos: currentEntry.photos.map(photo => ({
        imageUrl: photo.imageUrl || photo.photoPath || photo,
        title: photo.title || "",
        remarks: photo.remarks || "",
      }))
    };

    setCategories(prev => {
      const newCats = [...prev];
      if (editingEntryInfo) {
        // Update existing entry
        const { catIdx, entryIdx } = editingEntryInfo;
        const updatedEntries = [...newCats[catIdx].entries];
        updatedEntries[entryIdx] = entryWithPhotos;
        newCats[catIdx] = { ...newCats[catIdx], entries: updatedEntries };
      } else {
        // Add new entry
        const newEntries = [...newCats[selectedCategoryIdx].entries, entryWithPhotos];
        newCats[selectedCategoryIdx] = { ...newCats[selectedCategoryIdx], entries: newEntries };
      }
      return newCats;
    });

    setShowEntryForm(false);
    setEditingEntryInfo(null);
    // Reset current entry in draft
    setCurrentEntry({
      test: "",
      solutionType: "text",
      solutionText: "",
      solutionPoints: [""],
      solutionTable: [["", ""]],
      tableCols: 2,
      photos: [],
    });
  };

  // Cancel entry form
  const handleCancelEntry = () => {
    setShowEntryForm(false);
    setEditingEntryInfo(null);
  };

  // Add photo to current entry
  const handleAddPhotoToEntry = () => {
    setPhotoModalOpen(true);
  };

  // Add a new category or select an existing one
  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;

    const existingIdx = categories.findIndex(c => c.name.toLowerCase() === name.toLowerCase());

    if (existingIdx !== -1) {
      // Category already exists, select it and open the entry form
      setSelectedCategoryIdx(existingIdx);
      setShowEntryForm(true);
    } else {
      // Create a new category
      setCategories(prev => {
        const updated = [...prev, { name, entries: [] }];
        setSelectedCategoryIdx(updated.length - 1);
        return updated;
      });
      setShowEntryForm(true);
    }
    setNewCategoryName("");
  };

  // Delete a category (only allowed when more than 0 remain)
  const handleDeleteCategory = (idx) => {
    setCategories(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      setSelectedCategoryIdx(Math.max(0, idx - 1));
      return updated;
    });
    setShowEntryForm(false);
  };

  // Delete entry
  const handleDeleteEntry = (catIdx, entryIdx) => {
    const updatedEntries = categories[catIdx].entries.filter((_, i) => i !== entryIdx);
    setCategories(prev => {
      const newCats = [...prev];
      newCats[catIdx] = { ...newCats[catIdx], entries: updatedEntries };
      return newCats;
    });
  };

  // Drag and drop handlers for entries between categories
  const handleDragStart = (e, sourceCatIdx, sourceEntryIdx) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ sourceCatIdx, sourceEntryIdx }));
  };

  const handleDrop = (e, targetCatIdx) => {
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      const { sourceCatIdx, sourceEntryIdx } = JSON.parse(data);
      
      if (sourceCatIdx === targetCatIdx) return; // Do nothing if same category

      setCategories(prev => {
        const newCats = [...prev];
        const entryToMove = newCats[sourceCatIdx].entries[sourceEntryIdx];
        
        // Remove from source
        const updatedSourceEntries = newCats[sourceCatIdx].entries.filter((_, i) => i !== sourceEntryIdx);
        newCats[sourceCatIdx] = { ...newCats[sourceCatIdx], entries: updatedSourceEntries };
        
        // Add to target
        const updatedTargetEntries = [...newCats[targetCatIdx].entries, entryToMove];
        newCats[targetCatIdx] = { ...newCats[targetCatIdx], entries: updatedTargetEntries };
        
        return newCats;
      });
    } catch (err) {
      console.error("Error during drag and drop:", err);
    }
  };

  // Handle signature addition (drawing new signature)
  const handleAddSignature = async (signatureData, role) => {
    console.log("Adding signature for role:", role);

    const signatureWithData = {
      signature: signatureData.signature, // Base64 data URL
      name: signatureData.name || "",
      signedAt: new Date().toISOString(),
    };

    // For ADMIN/SUPERADMIN signing as PMC: Also sign as PMC Engineer with same signature
    const userRole = getUserRole();
    if ((userRole === "ADMIN" || userRole === "SUPERADMIN") && role === "pmc") {
      console.log("ADMIN signing as PMC - also signing as PMC Engineer");

      setSignatures((prev) => ({
        ...prev,
        [role]: signatureWithData,
        pmcEngineer: {
          signature: signatureData.signature,
          name: userData?.name || "PMC Engineer",
          signedAt: new Date().toISOString(),
        },
      }));

      // Save both signatures to UserSignature model
      await saveSignatureToUserProfile(signatureData, "pmc");
      await saveSignatureToUserProfile({
        ...signatureData,
        name: userData?.name || "PMC Engineer"
      }, "pmcEngineer");

      showToast("success", "Signatures Added", "PMC and PMC Engineer signatures added successfully.");
    } else {
      setSignatures((prev) => ({
        ...prev,
        [role]: signatureWithData,
      }));

      // Save signature to UserSignature model
      if (role === "pmcEngineer" || role === "pmc") {
        await saveSignatureToUserProfile(signatureData, role);
      }

      showToast("success", "Signature Added", `${role === "pmcEngineer" ? "PMC Engineer" : role === "siteEngineer" ? "Site Engineer" : "PMC"} signature added successfully.`);
    }
  };

  // Save signature to UserSignature model
  const saveSignatureToUserProfile = async (signatureData, role) => {
    try {
      console.log(`Saving signature to profile for ${role}`);

      const blob = dataURLtoBlob(signatureData.signature);
      const formData = new FormData();
      formData.append("signature", blob, "signature.png");

      const response = await axios.post(
        `${url}/api/users/signature/${userData._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Signature save response:", response.data);

      // Refresh the stored signature
      fetchUserSignature();

    } catch (err) {
      console.error("Error saving signature to profile:", err);
      showToast("error", "Save Failed", "Failed to save signature to profile");
    }
  };

  // Apply stored signature from UserSignature model
  const handleApplyStoredSignature = async (role) => {
    console.log(`Applying stored signature for ${role}`);

    let storedSignature = null;
    let userName = "";

    if (role === "pmcEngineer" && userSignature?.hasSignature) {
      storedSignature = userSignature.signature;
      userName = userSignature.userName || assignedEngineerName || "PMC Engineer";
      console.log("Found stored PMC Engineer signature");
    } else if (role === "pmc" && adminSignature?.hasSignature) {
      storedSignature = adminSignature.signature;
      userName = adminSignature.userName || "PMC";
      console.log("Found stored PMC signature");
    }

    if (storedSignature) {
      // Check if we need to convert file path to data URL
      let finalSignature = storedSignature;

      // If it's a file path (not a data URL), fetch and convert
      if (storedSignature.startsWith("uploads/")) {
        try {
          console.log("Converting file path to data URL:", storedSignature);
          const fullUrl = getFullImageUrl(storedSignature);

          const response = await fetch(fullUrl);
          const blob = await response.blob();

          // Convert blob to data URL
          finalSignature = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });

          console.log("File converted to data URL");
        } catch (error) {
          console.error("Error converting file to data URL:", error);
          // Fall back to using the file path
          finalSignature = storedSignature;
        }
      }

      const signatureWithData = {
        signature: finalSignature,
        name: userName,
        signedAt: new Date().toISOString(),
      };

      // For ADMIN/SUPERADMIN: When applying PMC signature, also apply to PMC Engineer
      const userRole = getUserRole();
      if ((userRole === "ADMIN" || userRole === "SUPERADMIN") && role === "pmc") {
        console.log("ADMIN applying stored PMC signature - also applying to PMC Engineer");

        setSignatures((prev) => ({
          ...prev,
          [role]: signatureWithData,
          pmcEngineer: {
            signature: finalSignature,
            name: userData?.name || "PMC Engineer",
            signedAt: new Date().toISOString(),
          },
        }));

        showToast("success", "Signatures Applied", "PMC and PMC Engineer signatures applied from stored signature.");
      } else {
        setSignatures((prev) => ({
          ...prev,
          [role]: signatureWithData,
        }));

        showToast("success", "Signature Applied", "Stored signature has been applied.");
      }
    } else {
      console.log(`No stored signature found for ${role}`);
      showToast("error", "No Signature", "No stored signature found. Please draw a new signature.");
    }
  };

  const clearPersistence = () => {
    // Clear both project name and the draft
    clearProjectName();
    clearPersistenceDraft();
    
    showToast("info", "Form Cleared", "Form data and drafts have been cleared.");
  };

  // Handle form submission
  const handleSubmit = async () => {
    console.log("=== FORM SUBMISSION ===");

    if (!projectName.trim()) {
      showToast("error", "Validation Error", "Please enter Project Name");
      return;
    }
    if (!location.trim()) {
      showToast("error", "Validation Error", "Please enter Location");
      return;
    }
    if (!dateOfVisit) {
      showToast("error", "Validation Error", "Please select Date of Visit");
      return;
    }
    if (!developers.trim()) {
      showToast("error", "Validation Error", "Please enter Developers");
      return;
    }
    if (!purposeOfVisit.trim()) {
      showToast("error", "Validation Error", "Please enter Purpose of Visit");
      return;
    }

    const formattedCategories = categories.map(cat => ({
      name: cat.name,
      entries: cat.entries.map((entry, idx) => ({
        title: entry.title || `Test ${idx + 1}`,
        details: entry.test,
        remarks: entry.solution,
        photos: entry.photos,
      })),
    }));
    

    // Validate that at least one entry exists in any category
    if (categories.every(cat => cat.entries.length === 0)) {
      showToast("error", "Validation Error", "Please add at least one entry");
      return;
    }

    // Initialize FormData before appending any fields
    const formData = new FormData();
    // Append categories payload
    formData.append("categories", JSON.stringify(formattedCategories));

    setLoading(true);

    try {
      // Add text fields
      formData.append("projectName", projectName);
      formData.append("location", location);
      formData.append("dateOfVisit", dateOfVisit.toISOString());
      formData.append("developers", developers);
      formData.append("pmc", pmc);
      formData.append("purposeOfVisit", purposeOfVisit);
      formData.append("reportDate", new Date().toISOString());

// Legacy entries payload is handled above when only a single "Uncategorized" category exists.
// No additional entries field is appended here.

      // Process signatures
      const formattedSignatures = {};

      // Helper to add signature to form data
      const addSignatureToFormData = (role, signatureData) => {
        formattedSignatures[role] = {
          name: signatureData?.name || "",
          signature: signatureData?.signature || "",
          signedAt: signatureData?.signedAt || new Date().toISOString(),
        };

        if (signatureData?.signature && signatureData.signature.startsWith("data:image")) {
          try {
            const blob = dataURLtoBlob(signatureData.signature);
            const filename = `${role}_signature_${Date.now()}.png`;
            const file = new File([blob], filename, { type: "image/png" });

            formData.append(`${role}Signature`, file);
            console.log(`${role} signature file appended`);
          } catch (err) {
            console.error(`Error converting ${role} signature:`, err);
          }
        }
      };

      // Add all signatures
      addSignatureToFormData("siteEngineer", signatures.siteEngineer);
      addSignatureToFormData("pmcEngineer", signatures.pmcEngineer);
      addSignatureToFormData("pmc", signatures.pmc);

      formData.append("signatures", JSON.stringify(formattedSignatures));

      if (userData?._id) {
        formData.append("userID", userData._id);
      }

      // Add photos as files for all category entries
      let entryIdx = 0;
      categories.forEach((cat) => {
        cat.entries.forEach((entry) => {
          entry.photos?.forEach((photo, photoIndex) => {
            if (photo.imageUrl && photo.imageUrl.startsWith("data:")) {
              try {
                const blob = dataURLtoBlob(photo.imageUrl);
                const file = new File(
                  [blob],
                  `photo_${entryIdx}_${photoIndex}.png`,
                  { type: "image/png" }
                );
                formData.append(`entries[${entryIdx}][photos]`, file);
              } catch (err) {
                console.error("Error converting photo:", err);
              }
            }
          });
          entryIdx++;
        });
      });

      console.log("Submitting form to:", `${url}/api/self-visit/add`);

      const response = await axios.post(
        `${url}/api/self-visit/add`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 60000,
        }
      );

      if (response.status === 201) {
        showToast("success", "Success!", "Site Visit report created successfully!");

        // Clear persistence on success
        clearPersistence();

        setTimeout(() => {
          navigate("/self-visit-reports");
        }, 1500);
      }
    } catch (error) {
      console.error("Error creating report:", error);
      showToast(
        "error",
        "Creation Failed",
        error.response?.data?.message || "Failed to create Site Visit report"
      );
    } finally {
      setLoading(false);
    }
  };

  const userRole = getUserRole();

  if (loadingSignatures || !isDraftLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 1 }}>
        {/* Header */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}>
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Add Site Visit Report
          </Typography>

          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: "red",
              "&:hover": {
                backgroundColor: "rgba(255, 0, 0, 0.1)",
              }
            }}
          >
            <Icon name="close" />
          </IconButton>
        </Box>

        {/* Signature Info Alert */}
        {/* <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {userRole === "ENGINEER" ? (
              "As an ENGINEER, you can sign as PMC Engineer. Site Engineer signature must be provided by the site engineer."
            ) : userRole === "ADMIN" || userRole === "SUPERADMIN" ? (
              "As an ADMIN, you can sign as both PMC and PMC Engineer. When you sign as PMC, both signatures will be applied automatically."
            ) : (
              "You do not have permission to sign reports."
            )}
          </Typography>
        </Alert> */}

        <Paper sx={{ p: 3, backgroundColor: "transparent", boxShadow: "none" }}>
          {/* Basic Information */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 3 }}>
              Basic Information
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Project Name *
                </Typography>
                <Autocomplete
                  value={projectName ? { projectName: projectName } : null}
                  onChange={handleProjectSelect}
                  filterOptions={filter}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  options={searchableProjects}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.inputValue) return option.inputValue;
                    return option.projectName;
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontWeight: 500 }}>{option.projectName}</Typography>
                        {!option.isNew && (
                          <Typography sx={{ fontSize: '11px', color: COLORS.LIGHT_SECONDARY }}>
                            {option.source} {option.clientName ? `• ${option.clientName}` : ''}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  sx={{
                    ...inputStyles,
                    "& .MuiAutocomplete-inputRoot": {
                      padding: "0 9px",
                      height: 40,
                      "& fieldset": { borderColor: COLORS.PRIMARY }
                    }
                  }}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search project or enter new"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Location *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter location"
                  size="small"
                  sx={inputStyles}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mt: 3 }}>
              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Date of Visit *
                </Typography>
                <DatePicker
                  value={dateOfVisit}
                  onChange={(newValue) => setDateOfVisit(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder="Select date"
                      sx={dateInputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small">
                              <Icon name="calendar2" color={COLORS.PRIMARY} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Developers *
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter developers"
                  size="small"
                  sx={inputStyles}
                  value={developers}
                  onChange={(e) => setDevelopers(e.target.value)}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mt: 3 }}>
              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  PMC
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter PMC"
                  size="small"
                  sx={inputStyles}
                  value={pmc}
                  onChange={(e) => setPmc(e.target.value)}
                />
              </Box>

              <Box>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                  Purpose of Visit *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter purpose of visit"
                  sx={inputStyles}
                  value={purposeOfVisit}
                  onChange={(e) => setPurposeOfVisit(e.target.value)}
                  required
                />
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
                Site Engineer Name
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter Site Engineer's name"
                size="small"
                sx={inputStyles}
                value={pocName}
                onChange={(e) => setPocName(e.target.value)}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* ── Entries Section ── */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 3 }}>
              Site Visit Entries
            </Typography>

            {/* ── No categories yet ── */}
            {categories.length === 0 && (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                  borderRadius: 1,
                  mb: 3,
                }}
              >
                <Typography sx={{ color: COLORS.TEXT, mb: 1 }}>
                  No categories yet
                </Typography>
                <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: 14 }}>
                  Type a category name below and click "+ Add Category" to get started
                </Typography>
              </Box>
            )}

            {/* ── Sequential Categories & Entries ── */}
            {categories.map((cat, catIdx) => (
              <Box 
                key={catIdx} 
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, catIdx)}
                sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}33`, borderRadius: 2, p: 3, mb: 4 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                    {cat.name}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteCategory(catIdx)}
                  >
                    <Icon name="delete" size={20} />
                  </IconButton>
                </Box>

                {/* Saved entries for this category */}
                {cat.entries.map((entry, index) => {
                  const isEditingThis = showEntryForm && selectedCategoryIdx === catIdx && editingEntryInfo && editingEntryInfo.entryIdx === index;
                  
                  if (isEditingThis) {
                    return (
                      <EntryForm
                        key={`edit-${index}`}
                        index={index}
                        data={currentEntry}
                        onDataChange={setCurrentEntry}
                        onSave={handleSaveEntry}
                        onCancel={handleCancelEntry}
                        onAddPhoto={handleAddPhotoToEntry}
                      />
                    );
                  }

                  return (
                    <DisplayEntry
                      key={index}
                      entry={entry}
                      index={index}
                      onEdit={() => handleEditEntry(catIdx, index)}
                      onDelete={(entryIdx) => handleDeleteEntry(catIdx, entryIdx)}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, catIdx, index)}
                    />
                  );
                })}

                {/* Entry Form (shown when adding a new entry to THIS category) */}
                {showEntryForm && selectedCategoryIdx === catIdx && !editingEntryInfo && (
                  <EntryForm
                    index={cat.entries.length}
                    data={currentEntry}
                    onDataChange={setCurrentEntry}
                    onSave={handleSaveEntry}
                    onCancel={handleCancelEntry}
                    onAddPhoto={handleAddPhotoToEntry}
                  />
                )}

                {/* Empty state for entries */}
                {cat.entries.length === 0 && (!showEntryForm || selectedCategoryIdx !== catIdx) && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ color: COLORS.TEXT, mb: 1 }}>
                      No entries added yet
                    </Typography>
                    <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: 14 }}>
                      Click "Add New Entry" to start adding test entries
                    </Typography>
                  </Box>
                )}

                {/* Add entry button */}
                {(!showEntryForm || selectedCategoryIdx !== catIdx) && (
                  <Box sx={{ display: "flex", justifyContent: cat.entries.length > 0 ? "flex-end" : "center", mt: 2 }}>
                    <Button
                      variant={cat.entries.length > 0 ? "outlined" : "contained"}
                      startIcon={<Icon name="plus2" />}
                      onClick={() => {
                        setSelectedCategoryIdx(catIdx);
                        setShowEntryForm(true);
                      }}
                      sx={
                        cat.entries.length > 0
                          ? {
                              borderColor: COLORS.SECONDARY,
                              color: COLORS.SECONDARY,
                              textTransform: "none",
                              "&:hover": { borderColor: COLORS.PRIMARY, color: COLORS.PRIMARY },
                            }
                          : {
                              bgcolor: COLORS.SECONDARY,
                              textTransform: "none",
                              "&:hover": { bgcolor: COLORS.PRIMARY },
                            }
                      }
                    >
                      {cat.entries.length > 0 ? "Add Another Entry" : "Add New Entry"}
                    </Button>
                  </Box>
                )}
              </Box>
            ))}

            {/* ── Add Category Row (At the bottom) ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 2 }}>
              <Autocomplete
                freeSolo
                options={categories.map(c => c.name)}
                value={newCategoryName}
                onInputChange={(e, newValue) => setNewCategoryName(newValue)}
                onChange={(e, newValue) => {
                  if (newValue) {
                    setNewCategoryName(newValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select or enter new category…"
                    size="small"
                    sx={{ ...inputStyles, width: 250 }}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddCategory(); }}
                  />
                )}
                sx={{ width: 250 }}
              />
              <Button
                variant="contained"
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim()}
                sx={{
                  bgcolor: COLORS.SECONDARY,
                  "&:hover": { bgcolor: COLORS.PRIMARY },
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                }}
              >
                + Add / Select Category
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Signatures Section */}
          <SignaturesDisplay
            signatures={signatures}
            onAddSignature={handleAddSignature}
            onApplyStoredSignature={handleApplyStoredSignature}
            userRole={userRole}
            userData={userData}
            pocName={pocName}
            assignedEngineerName={assignedEngineerName}
            userSignature={userSignature}
            adminSignature={adminSignature}
            loadingSignatures={loadingSignatures}
          />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6, gap: 3 }}>
            <Button
              variant="outlined"
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                minWidth: 150,
                color: COLORS.ERROR,
                borderColor: COLORS.ERROR,
                "&:hover": {
                  bgcolor: `${COLORS.ERROR}1A`,
                  borderColor: COLORS.ERROR,
                },
              }}
              onClick={clearPersistence}
              startIcon={<Icon name="delete" />}
              disabled={loading}
            >
              Clear Fields
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: COLORS.SECONDARY,
                borderRadius: 3,
                px: 4,
                py: 1.5,
                // textTransform: "none",
                minWidth: 200,
                "&:hover": {
                  bgcolor: COLORS.PRIMARY,
                },
              }}
              onClick={handleSubmit}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Icon name="add" />
                )
              }
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Site Visit Report"}
            </Button>
          </Box>
        </Paper>

        {/* Photo Upload Modal */}
        {photoModalOpen && (
          <PhotoUploadWithDrawing
            open={photoModalOpen}
            onClose={() => setPhotoModalOpen(false)}
            onSave={handlePhotoUpload}
            maxPhotos={5}
          />
        )}

        {/* Toast */}
        <AppToast
          open={toastOpen}
          type={toastType}
          title={toastTitle}
          message={toastMessage}
          onClose={handleToastClose}
          autoHideDuration={5000}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AddSelfVisitReport;